import { useEffect, useState } from "react";
import {
  contextRequest,
  type Context,
  type Project,
  type SourceParameters,
} from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";

/** One preview path for proofing and new bulk reviews; stale requests never win. */
export function useHostContext(
  project: Project,
  partId: string,
  demoProfile: string,
  enabled = true,
  versionId?: string,
  controlledParameters?: SourceParameters,
  onParametersChange?: (values: SourceParameters) => void,
) {
  const transport = useWorkspaceTransport(),
    { api } = transport;
  const [revision, refresh] = useState({ id: 0, force: false }),
    [localParameters, setParameters] = useState<SourceParameters>({}),
    [state, setState] = useState<{
      key: string;
      context?: Context;
      hash?: string;
      error?: string;
    }>();
  const suppliedParameters = controlledParameters ?? localParameters;
  const sourceParameters = Object.fromEntries(
    Object.entries(suppliedParameters).filter(([id]) =>
      project.dynamicSources?.some((source) => source.id === id),
    ),
  );
  useEffect(() => {
    if (JSON.stringify(suppliedParameters) === JSON.stringify(sourceParameters))
      return;
    if (onParametersChange) onParametersChange(sourceParameters);
    else setParameters(sourceParameters);
  }, [
    JSON.stringify(suppliedParameters),
    JSON.stringify(project.dynamicSources?.map((source) => source.id)),
  ]);
  useEffect(() => {
    if (controlledParameters === undefined) setParameters({});
  }, [project.id, versionId]);
  let requested: ReturnType<typeof contextRequest> | undefined,
    parameterError: string | undefined;
  try {
    requested = enabled
      ? contextRequest(
          project,
          {
            tenantId: project.tenantId,
            subject: transport.principal?.subject || "local-demo",
          },
          partId || undefined,
          { sourceParameters },
        )
      : undefined;
  } catch {
    parameterError = "Enter valid source parameters to load data.";
  }
  const needs =
      !!parameterError ||
      !!(requested && (requested.sources.length || requested.clock)),
    key = JSON.stringify([
      project,
      partId,
      demoProfile,
      revision,
      enabled,
      versionId,
      requested?.parameters,
      parameterError,
    ]),
    current = parameterError
      ? { key, error: parameterError, context: undefined, hash: undefined }
      : state?.key === key
        ? state
        : undefined;
  useEffect(() => {
    if (!needs || parameterError) return;
    const controller = new AbortController();
    // Effect replay or an immediate scope change cancels before issuing another request.
    const timer = setTimeout(() => {
      void api<{ context: Context; hash?: string }>("/context-preview", {
        method: "POST",
        body: JSON.stringify({
          ...transport.projectInput(project, versionId),
          partId: partId || undefined,
          ...(transport.demo ? { demoProfile } : {}),
          ...(requested?.parameters
            ? {
                sourceParameters: requested.parameters,
                forceRefresh: revision.force,
              }
            : {}),
        }),
        signal: controller.signal,
      })
        .then((snapshot) => {
          if (!controller.signal.aborted) setState({ key, ...snapshot });
        })
        .catch((error) => {
          if (!controller.signal.aborted)
            setState({ key, error: (error as Error).message });
        });
    }, 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [key, needs]);
  return {
    key,
    needs,
    current,
    context: current?.context,
    hash: current?.hash,
    sourceParameters: { ...sourceParameters, ...requested?.parameters },
    setSourceParameters: (values: SourceParameters, forceRefresh = false) => {
      if (onParametersChange) onParametersChange(values);
      else setParameters(values);
      refresh((previous) => ({ id: previous.id + 1, force: forceRefresh }));
    },
    blocked: needs && (!current || !!current.error),
    refresh: () =>
      refresh((previous) => ({ id: previous.id + 1, force: true })),
  };
}
